import { DateTime } from "luxon";
import {
  BrokerRecognizedEmployer,
  CountryIsoCode,
  EmploymentStatus,
  employmentStatuses,
  investmentObjectives,
  knowledgeLevels,
  maritalStatuses,
  relationships,
} from "@goodfill/domain-customer-application-types";
import z from "zod";
import { BusinessesAndOccupation } from "../../../backendApi/lambdas/individualApplication/listBusinessesAndOccupations";
import {
  zCountryIsoCode,
  zRequiredEmail,
  zMustAgree,
  zRequiredSocialSecurityNumber,
} from "../../../../form/zod/zodTypes";
import { formSourceOfWealthSchema } from "./formSourceOfWealth";
import { zAddressWithoutCountryScheme } from "../../../../form/zod/formAddress";
import { DefaultValues } from "react-hook-form";
import { zRequiredPhone } from "../../../../form/zod/formPhone";
import { zRequiredString, zRequiredBoolean, zRequiredNumber } from "@goodfill/domain-properties-zod-validators";

export const MAXIMUM_OF_SOURCES_OF_WEALTH = 9;

// The preprocess is here to hack-fix the limitation of the React-Hook-Forms (they lack support for incomplete form state).
// The React Hook Forms always set value to something (in this case to null) inside of the nested object, breaking the schema.
//
// See:
//  - https://github.com/react-hook-form/react-hook-form/issues/8046
//  - https://github.com/react-hook-form/react-hook-form/issues/9600
//  - https://stackoverflow.com/questions/73715295/react-hook-form-with-zod-resolver-optional-field/74932540
//  - https://github.com/react-hook-form/react-hook-form/discussions/8496
//
const zNullableObject = <T extends z.ZodRawShape>(
  schema: z.ZodObject<T> | z.ZodEffects<z.ZodObject<T>>,
  testField: keyof T,
) =>
  z.preprocess(
    (val) =>
      // rome-ignore lint/suspicious/noExplicitAny: <explanation>
      typeof val === "object" && val !== null && testField in val && (val as Record<keyof T, any>)[testField] === null
        ? null
        : val,
    z.union([z.null(), schema]),
  );

const zValidateSignature = (firstName: string, lastName: string) =>
  zRequiredString().refine((val) => val === `${firstName} ${lastName}`, { message: "signature must match your name" });

const maxDateOfBirth = () => DateTime.now().minus({ years: 18 }).toFormat("yyyy-MM-dd");

export const isEmployerCountryAbroadExplanationRequired = (
  countryOfResidence: CountryIsoCode,
  employerCountry: CountryIsoCode,
) => countryOfResidence && employerCountry && countryOfResidence !== employerCountry;

export const REQUIRED_EMPLOYMENT_DETAILS: EmploymentStatus[] = ["employed", "selfEmployed"];

export const isEmploymentDetailRequired = (status: EmploymentStatus | null) =>
  status === null || REQUIRED_EMPLOYMENT_DETAILS.includes(status);

const brokerCompanySchema = z.object({
  name: zRequiredString(),
  email: zRequiredEmail,
  phone: zRequiredPhone,
  address: zAddressWithoutCountryScheme,
});

export type BrokerCompany = z.infer<typeof brokerCompanySchema>;

export const selectedRecognizedEmployerTransform = {
  toState: (value: BrokerRecognizedEmployer | null | undefined): string | undefined =>
    value ? `${value.employerId}|||${value.employerName}` : undefined,

  toObject: (value: string | undefined): BrokerRecognizedEmployer | null => {
    if (!value) {
      return null;
    }

    const [employerId, employerName] = value.split("|||");

    return { employerId, employerName };
  },
};

const brokerSchema = z
  .object({
    duplicateStatementRequired: zRequiredBoolean(),
    brokerAffiliationRelationship: z.enum(relationships),
    brokerName: z.string(), // intentionally not required
    selectedRecognizedEmployer: zRequiredString(),
    brokerCompany: zNullableObject(brokerCompanySchema, "name"),
  })
  .refine((val) => val.brokerAffiliationRelationship === "self" || val.brokerName.trim().length > 0, {
    message: "required",
    path: ["brokerName"],
  });

export type CustomerApplicationFormStateBroker = z.infer<typeof brokerSchema>;
export const defaultBroker: DefaultValues<CustomerApplicationFormStateBroker> = {
  brokerCompany: null,
  brokerName: "",
  duplicateStatementRequired: undefined,
  brokerAffiliationRelationship: undefined,
  selectedRecognizedEmployer: undefined,
};

const employerScheme = z.object({
  name: zRequiredString(),
  country: zCountryIsoCode,
  // intentionally not required, depends on the `employer.country` !== `customers.country`
  abroadExplanation: z.string(),
});

export type CustomerApplicationEmployer = z.infer<typeof employerScheme>;

export const employmentScheme = z.object({
  occupation: z.custom<BusinessesAndOccupation>(
    (val) => typeof val === "object" && val !== null && "business" in val && "occupation" in val,
    { message: "required" },
  ),
  employer: employerScheme,
});

export type CustomerApplicationFormStateEmployment = z.infer<typeof employmentScheme>;

export const createCustomerApplicationFormStateSchema = (
  { firstName, lastName }: { firstName: string; lastName: string } = { firstName: "", lastName: "" },
) =>
  z.object({
    // SignUp
    // step1: separately in file

    // Personal (part 1)
    step2: z
      .object({
        dateOfBirth: zRequiredString().refine((val) => val <= maxDateOfBirth(), {
          message: "You must be at least 18 years old",
        }),
        maritalStatus: z.enum(maritalStatuses),
        numberOfDependents: zRequiredNumber().min(0, { message: "must be > 0" }),

        address: zAddressWithoutCountryScheme,

        sameMailingAddress: zRequiredBoolean(),
        mailingAddress: zNullableObject(zAddressWithoutCountryScheme, "street1"),

        // Documents
        issuingCountryOfIdentification: zCountryIsoCode,
        proofOfAddressFileId: zRequiredString("proof of address required"),
        proofOfIdentityFileFrontId: zRequiredString("ID-front required"),
        proofOfIdentityFileBackId: zRequiredString("ID-back required"),
      })
      .refine((val) => val.sameMailingAddress !== true || typeof val.mailingAddress === "object", {
        message: "required",
        path: ["mailingAddress"],
      }),

    // Personal (part 2)
    step3: z.object({
      socialSecurityNumber: zRequiredSocialSecurityNumber,
      countryOfBirth: zCountryIsoCode,
      countryOfCitizenship: zCountryIsoCode,

      ssnAcknowledgement: zMustAgree,
      ssnWithholdingAcknowledgement: zMustAgree,
      individualApplyingAcknowledgement: zMustAgree,
      usaTaxResidency: zMustAgree,
      trueAndCorrectSignature: zValidateSignature(firstName, lastName),
    }),

    // Employment
    step4: z
      .object({
        employmentStatus: z.enum(employmentStatuses),
        employment: zNullableObject(employmentScheme, "employer"),
      })
      .refine(
        (val) => {
          const required =
            val.employment &&
            isEmployerCountryAbroadExplanationRequired(
              "USA", // Currently intentionally hardcoded
              val.employment.employer.country as CountryIsoCode,
            );

          return !required || (val.employment?.employer?.abroadExplanation?.length ?? 0) > 0;
        },
        { message: "required", path: ["employment.employer.abroadExplanation"] },
      ),

    // Financial
    step5: z.object({
      investmentObjectives: z.array(z.enum(investmentObjectives)).min(1, { message: "required" }),
      annualNetIncome: zRequiredNumber().min(100, { message: "must be at least 100" }),
      liquidNetWorth: zRequiredNumber().min(100, { message: "must be at least 100" }),
      netWorth: zRequiredNumber().min(100, { message: "must be at least 100" }),

      sourcesOfWealth: z
        .array(formSourceOfWealthSchema)
        .min(1, "at least one Source of Wealth required")
        .max(MAXIMUM_OF_SOURCES_OF_WEALTH, "too many Sources of Wealth, the maximum is 9")
        .refine((val) => val.find((a) => a.isUsedForFunds) !== undefined, {
          message: "At least Source Of Wealth must be used for funds",
        }),
    }),

    // Step6StockAndBondExperience
    step6: z.object({
      tradesPerYearBonds: zRequiredNumber(),
      yearsExperienceBonds: zRequiredNumber(),
      bondsExperienceLevel: z.enum(knowledgeLevels),
    }),

    // Step7Regulatory
    step7: z
      .object({
        isBroker: zRequiredBoolean(),
        broker: zNullableObject(brokerSchema, "brokerName"),
        isEmployeeOfPubliclyTradedCompany: zRequiredBoolean(),
        employeeOfPubliclyTradedCompanyStockSymbols: z.array(z.string()),
        hasControlOfPubliclyTradedCompany: zRequiredBoolean(),
        controlOfPubliclyTradedCompanyStockSymbols: z.array(z.string()),
      })
      .refine(
        (val) =>
          val.isEmployeeOfPubliclyTradedCompany !== true ||
          val.employeeOfPubliclyTradedCompanyStockSymbols.length >= 1,
        { message: "required", path: ["employeeOfPubliclyTradedCompanyStockSymbols"] },
      )
      .refine(
        (val) =>
          val.hasControlOfPubliclyTradedCompany !== true || val.controlOfPubliclyTradedCompanyStockSymbols.length >= 1,
        { message: "required", path: ["controlOfPubliclyTradedCompanyStockSymbols"] },
      ),

    // Step8TradingPermissions
    step8: z.object({
      allowBondTrading: zRequiredBoolean(),
      allowChangingAccountInformationAfterUserInstruction: zRequiredBoolean(),
      allowTransmittingReceivedOrders: zRequiredBoolean(),
      allowConfirmAccountInformation: zRequiredBoolean(),
      allowRespondingToTenders: zRequiredBoolean(),
      allowRespondingToPutOptionsContingentUponCorporateAction: zRequiredBoolean(),
      allowRespondingToPutOptionsNotContingentUponCorporateAction: zRequiredBoolean(),
      allowOtherElectionsByBondHolders: zRequiredBoolean(),

      isInteractiveBrokersTermsConfirmed: zMustAgree,
      signature: zValidateSignature(firstName, lastName),
    }),
  });

export type CustomerApplicationFormState = z.infer<ReturnType<typeof createCustomerApplicationFormStateSchema>>;

export const formDefaultState: DefaultValues<CustomerApplicationFormState> = {};
